import { useLazyQuery } from '@apollo/client';
import { ClockIcon, MoonIcon } from '@heroicons/react/24/solid';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { InfoWindow, Map, useMap } from '@vis.gl/react-google-maps';
import { addMinutes } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Avatar from '../../components/Avatar/Avatar';
import Page from '../../components/Page/Page';
import { Col, Row } from '../../components/Styled/Container';
import {
  MapChip,
  MapContainer,
  MapControls,
  MapHeadline,
} from '../../components/Styled/MapChrome';
import { StyledLink } from '../../components/Styled/StyledLink';
import Text from '../../components/Styled/Text';
import { UserActivityMapResult } from '../../graphql/graphql';
import { STOREFRONT_MAP } from '../../graphql/queries-gql';
import { calculateAge, timeSinceString } from '../../util/helpers';
import NightOverlay from '../user-activity/NightOverlay';
import TimeZoneMarkers from '../user-activity/TimeZoneMarkers';
import StorefrontMarkers from './StorefrontMarkers';
import { COUNTRY_CENTERS } from './countryCoordinates';
import { getTierFromCountry } from './tiers';

const Layout = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 16px;

  @media (max-width: 1100px) {
    flex-direction: column;
  }
`;

const TierSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const TierPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 11px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.border};
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};

  strong {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${(props) => props.theme.textColor};
  }
`;

const TierDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => props.$color};
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  width: 248px;
  flex-shrink: 0;
  min-height: 0;
  background: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 1100px) {
    width: 100%;
    max-height: 260px;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 12px 14px;
  border-bottom: 1px solid ${(props) => props.theme.border};
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};

  span {
    font-size: 12px;
    font-weight: 500;
    color: ${(props) => props.theme.mutedTextColor};
  }
`;

const SidebarList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 6px;
`;

const StorefrontRow = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 34px;
  padding: 0 8px;
  border: 0;
  border-radius: 8px;
  background: ${(props) =>
    props.$selected ? props.theme.surfaceActive : 'transparent'};
  color: ${(props) => props.theme.textColor};
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${(props) =>
      props.$selected ? props.theme.surfaceActive : props.theme.surfaceHover};
  }
`;

const StorefrontTier = styled.span`
  flex: 1;
  font-size: 12px;
  font-weight: 400;
  color: ${(props) => props.theme.mutedTextColor};
`;

const StorefrontCount = styled.span`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

export type StorefrontUserData = {
  userId: string;
  username: string;
  lat: number;
  lng: number;
  photo?: {
    url?: string;
    thumbnailUrl?: string;
    uploadComplete?: boolean;
  } | null;
  country?: string | null;
  city?: string | null;
  created: string;
  birthDate?: string | null;
  currentAppVersion: string;
  premium: boolean;
  vip: boolean;
  appPlatform: string;
  lastActive: string;
  storeFront?: string | null;
  tier: string;
};

export type StorefrontGroup = {
  storeFront: string;
  tier: string;
  users: StorefrontUserData[];
  center: google.maps.LatLngLiteral;
};

const StorefrontMap = () => {
  const [minutes, setMinutes] = useState<number>(30);
  const [showNightOverlay, setShowNightOverlay] = useState<boolean>(true);
  const [showTimeZones, setShowTimeZones] = useState<boolean>(true);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [storefrontGroups, setStorefrontGroups] = useState<StorefrontGroup[]>([]);
  const [selectedStorefront, setSelectedStorefront] = useState<StorefrontGroup | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<StorefrontUserData[] | null>(null);
  const [infoPosition, setInfoPosition] = useState<google.maps.LatLngLiteral | null>(null);

  const map = useMap();

  const [fetchStorefrontMap] = useLazyQuery<{
    userActivityMap: UserActivityMapResult;
  }>(STOREFRONT_MAP, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const users = data.userActivityMap.data
        .filter((u) => u.lat != null && u.lng != null && !(u.lat === 0 && u.lng === 0))
        .map((u) => {
          const storeFront = (u as any).storeFront as string | null | undefined;
          const tier = getTierFromCountry(storeFront || u.country);
          return {
            ...u,
            storeFront: storeFront || u.country || 'Unknown',
            tier,
          };
        }) as StorefrontUserData[];

      setTotalUsers(data.userActivityMap.data.length);

      // Group by storeFront
      const groups: Record<string, StorefrontUserData[]> = {};
      for (const user of users) {
        const sf = user.storeFront || 'Unknown';
        if (!groups[sf]) groups[sf] = [];
        groups[sf].push(user);
      }

      // Position markers at country centers
      const sfGroups: StorefrontGroup[] = [];
      Object.entries(groups).forEach(([storeFront, groupUsers]) => {
        const countryCenter = COUNTRY_CENTERS[storeFront.toUpperCase()];
        const center = countryCenter ?? {
          lat: groupUsers.reduce((sum, u) => sum + u.lat, 0) / groupUsers.length,
          lng: groupUsers.reduce((sum, u) => sum + u.lng, 0) / groupUsers.length,
        };
        sfGroups.push({
          storeFront,
          tier: groupUsers[0].tier,
          users: groupUsers,
          center,
        });
      });

      sfGroups.sort((a, b) => b.users.length - a.users.length);
      setStorefrontGroups(sfGroups);
    },
  });

  useEffect(() => {
    fetchStorefrontMap({
      variables: {
        args: {
          minutes,
          filters: [],
          includeSignups: false,
          includeSignupReady: false,
        },
      },
    });

    const timer = minutes <= 30 ? 30000 : 1000 * 60 * 5;
    const interval = setInterval(() => {
      fetchStorefrontMap({
        variables: {
          args: {
            minutes,
            filters: [],
            includeSignups: false,
            includeSignupReady: false,
          },
        },
      });
    }, timer);

    return () => clearInterval(interval);
  }, [minutes, fetchStorefrontMap]);

  const handleStorefrontClick = useCallback((group: StorefrontGroup) => {
    setSelectedStorefront(group);
    setExpandedUsers(group.users);
    setInfoPosition(group.center);
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedStorefront(null);
    setExpandedUsers(null);
    setInfoPosition(null);
  }, []);

  const totalOnMap = useMemo(
    () => storefrontGroups.reduce((sum, g) => sum + g.users.length, 0),
    [storefrontGroups],
  );

  // Group storefronts by tier for the summary
  const tierSummary = useMemo(() => {
    const tiers: Record<string, { count: number; storefronts: string[] }> = {};
    for (const group of storefrontGroups) {
      if (!tiers[group.tier]) tiers[group.tier] = { count: 0, storefronts: [] };
      tiers[group.tier].count += group.users.length;
      tiers[group.tier].storefronts.push(group.storeFront);
    }
    return tiers;
  }, [storefrontGroups]);

  const resetMap = () => {
    map?.setZoom(2.4);
    map?.setCenter({
      lat: 35.613266732528366,
      lng: 9.880013000000014,
    });
    handleInfoWindowClose();
  };

  return (
    <Page
      fill
      title="Storefront map"
      actions={
        <ToggleButtonGroup
          value={minutes}
          exclusive
          size="small"
          onChange={(_, newValue) => {
            if (newValue !== null) setMinutes(newValue);
          }}
        >
          <ToggleButton value={5}>5m</ToggleButton>
          <ToggleButton value={15}>15m</ToggleButton>
          <ToggleButton value={30}>30m</ToggleButton>
          <ToggleButton value={60}>1h</ToggleButton>
          <ToggleButton value={180}>3h</ToggleButton>
          <ToggleButton value={480}>8h</ToggleButton>
          <ToggleButton value={1440}>24h</ToggleButton>
        </ToggleButtonGroup>
      }
      toolbar={
        <>
          <MapHeadline style={{ marginRight: 'auto' }}>
            <strong>{totalUsers.toLocaleString()}</strong>
            <span>
              users active in last {timeSinceString(addMinutes(new Date(), -minutes))}
            </span>
          </MapHeadline>
          <TierSummary>
            {['US', 'A', 'B', 'C', 'D', 'Unknown'].map((tier) => {
              const data = tierSummary[tier];
              if (!data) return null;
              return (
                <TierPill key={tier}>
                  <TierDot $color={TIER_LABEL_COLORS[tier]} />
                  Tier {tier}
                  <strong>{data.count.toLocaleString()}</strong>
                </TierPill>
              );
            })}
          </TierSummary>
        </>
      }
    >
      <Layout>
        <MapContainer>
          <MapControls>
            <MapChip as="div">
              {totalOnMap.toLocaleString()} on map{' '}
              {totalUsers > 0 && `(${Math.round((totalOnMap / totalUsers) * 100)}%)`}
            </MapChip>
            <MapChip
              $active={showTimeZones}
              onClick={() => setShowTimeZones((v) => !v)}
              title={showTimeZones ? 'Hide time zones' : 'Show time zones'}
            >
              <ClockIcon />
              {showTimeZones ? 'Time' : 'Time off'}
            </MapChip>
            <MapChip
              $active={showNightOverlay}
              onClick={() => setShowNightOverlay((v) => !v)}
              title={showNightOverlay ? 'Hide night overlay' : 'Show night overlay'}
            >
              <MoonIcon />
              {showNightOverlay ? 'Night' : 'Night off'}
            </MapChip>
            <Tooltip title="Reset map view">
              <MapChip aria-label="Reset map view" onClick={resetMap}>
                <CenterFocusWeakIcon style={{ width: 18, height: 18 }} />
              </MapChip>
            </Tooltip>
          </MapControls>
          <Map
            mapId="2465c920699db95c"
            style={{ width: '100%', height: '100%' }}
            defaultZoom={2.6}
            defaultCenter={{ lat: 35.613266732528366, lng: 9.880013000000014 }}
            onCameraChanged={() => {}}
          >
            <StorefrontMarkers
              groups={storefrontGroups}
              onStorefrontClick={handleStorefrontClick}
            />
            <NightOverlay enabled={showNightOverlay} />
            <TimeZoneMarkers enabled={showTimeZones} />

          {expandedUsers && infoPosition && selectedStorefront && (
            <InfoWindow position={infoPosition} onCloseClick={handleInfoWindowClose}>
              <Col
                padding={0}
                style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                <Row style={{ marginBottom: '10px' }}>
                  <Text size={16} weight={600} color={'#000'}>
                    {selectedStorefront.storeFront} (Tier {selectedStorefront.tier}),{' '}
                    {expandedUsers.length} users
                  </Text>
                </Row>
                {expandedUsers.map((user, index) => (
                  <Col
                    key={user.userId}
                    padding={0}
                    style={{
                      marginBottom: index < expandedUsers.length - 1 ? '20px' : '0',
                      paddingBottom: index < expandedUsers.length - 1 ? '20px' : '0',
                      borderBottom:
                        index < expandedUsers.length - 1 ? '1px solid #ccc' : 'none',
                    }}
                  >
                    <Row>
                      <Avatar
                        name={user.username ?? ''}
                        url={user.photo?.uploadComplete ? (user.photo?.thumbnailUrl ?? '') : ''}
                        sx={{ width: '60px', height: '60px' }}
                      />
                    </Row>
                    <Row>
                      <StyledLink to={`/user/${user.userId}`}>
                        <Text size={16} weight={600} color={'#000'}>
                          {user.username} ({calculateAge(user.birthDate ?? undefined)})
                        </Text>
                      </StyledLink>
                    </Row>
                    <Row>
                      <Text size={14} weight={400} color={'#000'}>
                        {user.city} | {user.country}
                      </Text>
                    </Row>
                    <Row>
                      <Text size={12} weight={400} color={'#000'}>
                        Active {timeSinceString(new Date(user.lastActive))} | Created{' '}
                        {timeSinceString(new Date(user.created))}
                      </Text>
                    </Row>
                    <Row>
                      <Text size={12} weight={400} color={'#000'}>
                        {user.currentAppVersion ?? 'N/A'} | {user.appPlatform ?? 'N/A'}
                      </Text>
                    </Row>
                    {user.premium && (
                      <Row>
                        <Text size={12} weight={600} color={'#000'}>
                          Premium
                        </Text>
                        {user.vip && (
                          <Text size={12} weight={600} color={'#000'}>
                            {' | '} VIP
                          </Text>
                        )}
                      </Row>
                    )}
                  </Col>
                ))}
                <Row
                  style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ccc' }}
                >
                  <StyledLink to="" onClick={resetMap}>
                    Reset map
                  </StyledLink>
                </Row>
              </Col>
            </InfoWindow>
          )}
          </Map>
          <NightOverlay enabled={showNightOverlay} />
        </MapContainer>

        <Sidebar>
          <SidebarHeader>
            Storefronts
            <span>{storefrontGroups.length}</span>
          </SidebarHeader>
          <SidebarList>
            {storefrontGroups.map((group) => (
              <StorefrontRow
                key={group.storeFront}
                $selected={selectedStorefront?.storeFront === group.storeFront}
                onClick={() => {
                  map?.setCenter(group.center);
                  map?.setZoom(4);
                  handleStorefrontClick(group);
                }}
              >
                <TierDot $color={TIER_LABEL_COLORS[group.tier] || '#95a5a6'} />
                {group.storeFront}
                <StorefrontTier>Tier {group.tier}</StorefrontTier>
                <StorefrontCount>{group.users.length}</StorefrontCount>
              </StorefrontRow>
            ))}
          </SidebarList>
        </Sidebar>
      </Layout>
    </Page>
  );
};

const TIER_LABEL_COLORS: Record<string, string> = {
  US: '#e74c3c',
  A: '#e67e22',
  B: '#f1c40f',
  C: '#2ecc71',
  D: '#3498db',
  Unknown: '#95a5a6',
};

export default StorefrontMap;
